// The live deal line and card (pipeline P5; DEALS-LIVE-CARD-BRIEF-2026-10-04.md section 5 items 6 and 7, drawn in
// a-deals.html; the states in states.html). Mike, 4 Oct 2026: "A deal card if you use Kaizen for example is expandable
// for light overview, however its design is static, what happens when there is a new email from them about the deal, or
// internal teams chat or what stage is it at, proposal sent contracting. This then has to link back to the company
// record so that the information is mirrored."
//
// Each opportunity as a line, and opened as a card that leads with the step (PRODUCT.md principle 1), with keenness and
// the year's figure in its head, the newest messages filed under it, the stage, and what Cobrain changed with one press
// to undo it (U231). Deals draws them and the company record imports the same (U240), from the same read,
// GET /api/deals/heat, so an edit on either is on the other at the next read. Pipeline P8 (DEALS-ONE-LIST-BRIEF-2026-10-05)
// made Deals one line per opportunity with an owner column, and an opened line the card beside the account's products.
//
// U152 is the rule: a control is drawn only where its route exists. "Give the deal to someone" hands a deal somebody
// holds to a colleague (POST /api/deals/:id/hand, U262) and gives one nobody holds (the give of U156); "Why is this
// here?" is built from this read alone (U263; pipeline P6, REQ-2026-10-04-25). "Everything on this deal" opens the history
// page, DealHistory.tsx (step 7).
//
// Pipeline P9 (REQ-2026-10-05-14; CLIENT-WORDS-AND-VALUE-BRIEF-2026-10-05.md section 4, c-card.html; U292 to U297): the
// line carries the client's latest words under its status, and a silent deal's silence in its place; the figure reads
// "[£] a year expected"; an opened line hides its status. The card leads with one keenness line (the five buttons open
// on Change), then the step box, "In their words" and Value (CardParts.tsx). The company record's line carries the
// latest words too.
import { ActionInput, ActionSelect, ActionButton, ActionRow, LineOrName, CompanyLogo as Logo } from '../parts/index.js';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { dayMonth } from '../day.js';
import { dealsApi, DealsError, usualAnswerLabel, type DealMessage, type Heat, type HeatRow, type Lead, type LiveOpportunity, type NewToday, type ShowLine, type StageStep } from './api.js';
import { firstName, moneyFull } from './heat-words.js';
import { accountPossessive, dayWords, decisionUndoable, decisionWords, handedWords, holderWords, keenClass, keennessByWords, kindChip, latestHead, meterSteps, moveSentence, moveStillSays, newsKind, newTodayWords, opensPath, ownerOf, parkedWords, productsCountWords, rowStep, senderWords, shownSilence, silentWords, stageTone, stepBoxWords, subjectWords, undoable, whyHereWords, type LineRank, type StepWords } from './live-words.js';
import { keennessLine, lineWords, silenceWords } from './client-words.js';
import { InTheirWords, ValueBlock } from './CardParts.js';
import { DealOptions } from '../dealwork/DealOptions.js';
import { SameDeal } from '../dealwork/SameDeal.js';
import { useCompanyLine } from '../company/lines.js';
import { DealNotes } from './DealNotes.js';
import { CardMessage, useCardThread } from '../reply/CardMessage.js';
import { canReplyAll, ReplyBox } from '../reply/ReplyBox.js';
import { WHATSAPP_REPLY_SOON } from '../ItemCard.js';
import { CloseBox } from './CloseBox.js';
import type { CloseKind } from './close-api.js';

/** What the line and the card read beside their own rows: the rest of the heat read, and where they are drawn. */
export interface LiveContext {
  store: string;
  now: string;
  dayStart: string | null;
  homeCurrency: string | null;
  leads: Record<string, Lead>;
  stages: StageStep[];
  shows: ShowLine[];
  /** The product families the rings add up (crm.product_families()): the card's Product choices. */
  families: string[];
  /** Every currency a figure in the store is in, the home one first: the figure's choices. */
  currencies: string[];
  mayEdit: boolean;
  /** The reader's principal in this store, and who "Give the deal to someone" offers (U262): the whole team. */
  me: string;
  giveTo: Array<{ principalId: string; name: string }>;
  /** Deals, or the company record (U240): the record's card says "See it on Deals" in place of "Open the company". */
  place: 'deals' | 'company';
  open: (path: string) => void;
  /**
   * Read the page again quietly after a write; the same read the company record and Deals both draw from. `after` names
   * the card written, so the page can keep it in view when the re-read moves it (a keenness taking an account to
   * another band, or a line to another place in the record's list).
   */
  refresh: (after?: { accountId: string; dealId: string }) => void;
  /** What each card last said, kept by the page (useCardNotes), so a card the re-read moves or draws anew still says it. */
  notes: Record<string, CardNote>;
  setNote: (dealId: string, note: CardNote | null) => void;
}

/** What a card last said: "Saved" with its Undo, or a line about a write that did not take or an undo. */
export interface CardNote { saved: Saved | null; note: string | null }

/**
 * The cards' notes, held by the page and not by each card. A keenness set to 3 moves its opportunity behind "Also at"
 * and can move its account to another band; the card was drawn anew there and its "Saved" and Undo were gone with the
 * card's own state (found pressing it on live, 4 Oct 2026).
 */
export function useCardNotes(): Pick<LiveContext, 'notes' | 'setNote'> {
  const [notes, setNotes] = useState<Record<string, CardNote>>({});
  const setNote = useCallback((dealId: string, note: CardNote | null) => setNotes((was) => {
    const next = { ...was };
    if (note) next[dealId] = note; else delete next[dealId];
    return next;
  }), []);
  return { notes, setNote };
}

const itemPath = (lead: Lead, store: string) => `/items/${encodeURIComponent(lead.passItemId)}?store=${encodeURIComponent(store)}`;

export function Chev() {
  return <svg className="lv-chev" viewBox="0 0 16 16" aria-hidden="true"><path d="M6 3l5 5-5 5" fill="none" stroke="currentColor" strokeWidth="2" /></svg>;
}

/** The keenness block: the numeral on its navy step (U268), "–" on the neutral block when none is recorded. */
export function KeenBlock({ k, small }: { k: number | null; small?: boolean }) {
  return <span className={`lv-kb ${keenClass(k)}${small ? ' sm' : ''}`} aria-label={k === null ? 'No keenness yet' : `Keenness ${k}`}>{k ?? '–'}</span>;
}

/**
 * A line's status (P10; DEALS-COLOUR-BRIEF-2026-10-05.md section 4a item 5): its lead words in magenta where the move is
 * ours ("Waiting on us:", "Your step:", the reader's own carrying the ink "You" mark), the rest in ink-2; the show notes'
 * step in grey. Never the private violet box: violet is for what is private (D77), and "yours" is not ".mine".
 */
export function Status({ s }: { s: StepWords }) {
  return (
    <span className={s.notes ? 'lv-stp notes' : s.you ? 'lv-stp yours' : 'lv-stp'} data-kind={s.kind}>
      {s.you ? <span className="who">You</span> : null}
      {s.lead ? <><span className="lead">{s.lead}</span> {s.rest}</> : s.rest}
    </span>
  );
}

/** What is new today on a line, its weight by whose it is (U307): the client's in ink, ours the lightest, with a hollow dot. */
function News({ n }: { n: NewToday | null }) {
  return n
    ? <span className="lv-nw" data-kind={newsKind(n)}><span className="dot" />{newTodayWords(n)}</span>
    : <span className="lv-nw none">Nothing new</span>;
}

/** The five-segment stage track of a line (a-deals.html), by the store's own stages. */
export function Track({ stages, code }: { stages: StageStep[]; code: string | null }) {
  return <span className="lv-track" aria-hidden="true">{meterSteps(stages, code).map((s) => <i key={s.code} className={s.state === 'ahead' ? '' : s.state} />)}</span>;
}

/** What the company record's line draws for one opportunity (a-company.html, U240). */
interface LineParts {
  keenness: number | null;
  name: string;
  owner: string;
  step: StepWords;
  /** The reader's own step, in yellow under the step while the line is closed (U216). */
  yellow: { lead: Lead; fallback: string } | null;
  live: LiveOpportunity | null;
  silentDays: number | null;
  newToday: NewToday | null;
  figure: ReactNode;
  /** The client's latest words, with who, the channel and when (P9, U296). */
  words: { words: string; source: string } | null;
}

/**
 * The company record's line, in two rows (a-company.html): the keenness block; the name and whose it is, the status
 * under it, in ink when the step is the reader's, and under that the reader's own yellow at every width; the stage and
 * what is new today, or the silence chip (the only red on a line); the revenue potential; the chevron. A press anywhere
 * on it opens it.
 */
function LineView({ parts, ctx, isOpen, toggle, bodyId }: { parts: LineParts; ctx: LiveContext; isOpen: boolean; toggle: () => void; bodyId: string }) {
  const { step, yellow, live } = parts;
  return (
    <div className="lv-line" data-stage={stageTone(live?.stageCode)} onClick={toggle}>
      <KeenBlock k={parts.keenness} />
      <span className="lv-main">
        <ActionButton reading type="button" className="lv-an" aria-expanded={isOpen} aria-controls={bodyId} onClick={(e) => { e.stopPropagation(); toggle(); }}>{parts.name}</ActionButton>
        <span className="lv-ao">{parts.owner}</span>
        <Status s={step} />
        {parts.words ? <span className="lv-their">{parts.words.words} <span className="src">{parts.words.source}</span></span> : null}
        {yellow && !isOpen
          ? (
            <span className="lv-lead-row">
              <ActionButton reading type="button" className="opt lead" onClick={(e) => { e.stopPropagation(); ctx.open(itemPath(yellow.lead, ctx.store)); }}>
                {yellow.lead.options[0]?.label ?? yellow.fallback}{yellow.lead.options[0]?.note ? <small>{yellow.lead.options[0].note}</small> : null}
              </ActionButton>
            </span>
          )
          : null}
      </span>
      <span className="lv-sn">
        <span className="lv-st"><span className="stw">{live?.stageName ?? 'No stage'}</span> <Track stages={ctx.stages} code={live?.stageCode ?? null} /></span>
        {parts.silentDays !== null
          ? <span className="lv-silent">{silentWords(parts.silentDays)}</span>
          : <News n={parts.newToday} />}
      </span>
      {parts.figure}
      <Chev />
    </div>
  );
}

/** One opportunity's line on the company record (a-company.html, U240): its own step, stage, news and figure. */
export function OpportunityLineView({ row, ctx, isOpen, toggle, bodyId }: { row: HeatRow; ctx: LiveContext; isOpen: boolean; toggle: () => void; bodyId: string }) {
  const lead = row.status === 'step' && row.step?.owner?.mine ? ctx.leads[row.id] ?? null : null;
  const figure = <RowFigure row={row} />;
  const show = row.eventId ? ctx.shows.find((s) => s.id === row.eventId) ?? null : null;
  return (
    <LineView ctx={ctx} isOpen={isOpen} toggle={toggle} bodyId={bodyId} parts={{
      words: lineWords(row, show?.name ?? null, ctx.dayStart, false),
      keenness: row.keenness, name: row.product ?? row.title, owner: holderWords(row), step: rowStep(row),
      yellow: lead ? { lead, fallback: row.step?.title ?? 'Open your step' } : null,
      live: row.live, silentDays: shownSilence(row, ctx.now), newToday: row.newToday, figure,
    }} />
  );
}

/** The figure slot of an opportunity's line: its year's figure, or why it has none. Deals' says "a year expected" (P9). */
function RowFigure({ row, expected }: { row: HeatRow; expected?: boolean }) {
  return row.figure
    ? <span className={expected ? 'lv-fig exp' : 'lv-fig'}>{moneyFull(row.figure.amount, row.figure.currency)}{expected ? ' a year expected' : ''}</span>
    : <span className="lv-fig none">{row.suppliesUs ? 'Supplies us' : 'No figure yet'}</span>;
}

/** The warning triangle beside a silence (c-card.html). */
function WarnIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true"><path d="M12 3l10 18H2z" /><path d="M12 10v5M12 18v.5" /></svg>
  );
}

/**
 * One line of Deals (DEALS-ONE-LIST-BRIEF-2026-10-05.md section 3; module 10 U278 to U280), left to right: the keenness
 * block; the account and the product; the owner, with the "You" mark for the reader; the stage as a word and the
 * five-step meter; the status, with the reader's own yellow under it while the line is closed (U216); what is new today
 * or the silence chip; the year's figure, or "No figure yet"; the chevron. Under 1300 wide the status and the news take a
 * second row, and on a phone the line is P5's two rows with the owner's first name in the second. A press anywhere on it
 * opens it.
 */
export function DealsLine({ row, ctx, isOpen, toggle, bodyId }: { row: HeatRow; ctx: LiveContext; isOpen: boolean; toggle: () => void; bodyId: string }) {
  const step = rowStep(row);
  const owner = ownerOf(row);
  const lead = step.you ? ctx.leads[row.id] ?? null : null;
  const live = row.live;
  const show = row.eventId ? ctx.shows.find((s) => s.id === row.eventId) ?? null : null;
  const silentDays = shownSilence(row, ctx.now);
  const silent = silentDays !== null;
  const words = lineWords(row, show?.name ?? null, ctx.dayStart, silent);
  return (
    <div className="lv-line lv-oline" data-stage={stageTone(live?.stageCode)} onClick={toggle}>
      <KeenBlock k={row.keenness} />
      <span className="lv-on co-with-logo">
        {/* The logo of the company the deal is filed on (pipeline RC8; U359, U371), beside the name and not in it, so the
            name's words stay the button's own. */}
        {/* BUGS.md D97: a company put away is named nowhere, so it has no logo or initials here either. */}
        {row.organisationName !== null ? <Logo name={row.organisationName} logo={row.organisationLogo ?? null} size="sm" /> : null}
        <ActionButton reading type="button" className="lv-an" aria-expanded={isOpen} aria-controls={bodyId} onClick={(e) => { e.stopPropagation(); toggle(); }}>
          {/* The product beside the account, quieter, with no middle dot between them (P10, section 4a item 2); an account
              put away is not named (D97), and the product stands alone. */}
          {row.product ? <>{row.accountName !== null ? <>{row.accountName} </> : null}<span className="pr">{row.product}</span></> : row.title}
        </ActionButton>
      </span>
      <span className={owner.you ? 'lv-ow you' : row.holder ? 'lv-ow' : 'lv-ow none'}>{owner.you ? 'You' : owner.full}</span>
      <span className="lv-st"><span className="ofn">{owner.first}</span><span className="stw">{live?.stageName ?? 'No stage'}</span> <Track stages={ctx.stages} code={live?.stageCode ?? null} /></span>
      <span className="lv-ss">
        {/* A silent deal shows its silence where the status was (c-card.html); an opened line hides the status, the card's step box says it. */}
        {silent
          ? <span className="lv-silence"><WarnIcon />{silenceWords(silentDays!, live?.stageCode ?? null)}</span>
          : isOpen ? null : <Status s={step} />}
        {words ? <span className="lv-their">{words.words} <span className="src">{words.source}</span></span> : null}
        {lead && !isOpen
          ? (
            <span className="lv-lead-row">
              <ActionButton reading type="button" className="opt lead" onClick={(e) => { e.stopPropagation(); ctx.open(itemPath(lead, ctx.store)); }}>
                {lead.options[0]?.label ?? row.step?.title ?? 'Open your step'}{lead.options[0]?.note ? <small>{lead.options[0].note}</small> : null}
              </ActionButton>
            </span>
          )
          : null}
      </span>
      <span className="lv-news"><News n={row.newToday} /></span>
      <RowFigure row={row} expected />
      <Chev />
    </div>
  );
}

/** The card's context from a heat read: Deals and the company record build it alike, so the two cannot drift. */
export function liveContextOf(
  heat: Heat, store: string, place: LiveContext['place'], open: (path: string) => void, refresh: LiveContext['refresh'],
  kept: Pick<LiveContext, 'notes' | 'setNote'>,
): LiveContext {
  const home = heat.homeCurrency;
  const rows = heat.lines;
  const currencies = [...new Set([home, ...heat.rings.flatMap((r) => r.other.map((o) => o.currency)), ...rows.map((r) => r.figure?.currency ?? null)].filter((c): c is string => !!c))];
  return {
    store, now: heat.now, dayStart: heat.dayStart, homeCurrency: home, leads: heat.leads, stages: heat.stages, shows: heat.shows,
    families: heat.rings.map((r) => r.family), currencies, mayEdit: heat.mayEdit, me: heat.me, giveTo: heat.giveTo ?? [], place, open, refresh, ...kept,
  };
}

/**
 * "Saved", Undo, and that the company record shows it too (states.html, "Saved"). `changeId` is what Undo names: a
 * change in the deal's own log, or, for `field: 'holder'`, the give's audit.action (undone by the allocation's undo).
 */
export interface Saved { field: string; changeId: string; words: string; also?: string[]; undoUntil?: string }

function SavedNote({ row, saved, accountName, ctx, onUndone }: { row: HeatRow; saved: Saved; accountName: string | null; ctx: LiveContext; onUndone: (w: string | null) => void }) {
  const [busy, setBusy] = useState(false);
  const undo = () => {
    setBusy(true);
    // A write of two changes (how it pays, and what we expect: P9) is taken back whole, the newer first.
    (saved.field === 'holder' ? dealsApi.undoAllocation(ctx.store, row.id, saved.changeId)
      : (saved.also ?? []).reduce((p, id) => p.then(() => dealsApi.undoChange(ctx.store, id)), Promise.resolve(null as unknown)).then(() => dealsApi.undoChange(ctx.store, saved.changeId)))
      .then(() => { onUndone(null); ctx.refresh({ accountId: row.accountId, dealId: row.id }); })
      .catch((e: unknown) => { setBusy(false); onUndone(e instanceof DealsError ? `It was not undone: ${e.message}.` : 'It was not undone. Try again.'); });
  };
  const elsewhere = ctx.place === 'deals' ? `${accountPossessive(accountName)} record shows it too.` : 'Deals shows it too.';
  return (
    <p className="lv-savedline" role="status">
      <span className="saved">Saved</span>
      <ActionButton type="button" className="hx-link" disabled={busy} onClick={undo}>Undo</ActionButton>
      <span className="by">{saved.words} {elsewhere}</span>
    </p>
  );
}

/** A write that did not save says so and keeps what was typed (the brief's checklist; states.html). */
function SaveError({ words, retry }: { words: string; retry: () => void }) {
  return <p className="lv-err" role="alert">{words} <ActionButton type="button" className="hx-link" onClick={retry}>Try again</ActionButton></p>;
}

const failed = (e: unknown, kept: string): string => `It did not save${e instanceof DealsError ? `: ${e.message}` : ''}. ${kept}`;

/** Keenness as five buttons, the set one filled in its navy step (U268); a press asks one line why before it saves (U241). */
function KeennessPicker({ row, ctx, onSaved }: { row: HeatRow; ctx: LiveContext; onSaved: (s: Saved) => void }) {
  const [pending, setPending] = useState<number | null>(null);
  const [why, setWhy] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = () => {
    if (pending === null || !why.trim()) return;
    setBusy(true); setError(null);
    dealsApi.setKeenness(ctx.store, row.id, pending, why.trim())
      .then((r) => { setBusy(false); setPending(null); setWhy(''); onSaved({ field: 'keenness', changeId: r.changeId, words: `You set ${pending} just now.` }); ctx.refresh({ accountId: row.accountId, dealId: row.id }); })
      .catch((e: unknown) => { setBusy(false); setError(failed(e, 'Your reason is still in the box.')); });
  };
  return (
    <>
      <span className="lv-kpick" role="group" aria-label="Keenness">
        {[1, 2, 3, 4, 5].map((n) => {
          const on = (pending ?? row.keenness) === n;
          return (
            <ActionButton type="button" key={n} className={on ? `on ${keenClass(n)}` : ''} aria-pressed={on}
              onClick={() => { if (n === row.keenness) { setPending(null); setWhy(''); } else setPending(n); setError(null); }}>{n}</ActionButton>
          );
        })}
      </span>
      {pending !== null
        ? (
          <span className="lv-why">
            <label>
              <span className="l">Why {pending}?</span>
              <ActionInput type="text" value={why} maxLength={500} autoFocus placeholder="One line: what the client said or did"
                onChange={(e) => setWhy(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setPending(null); setWhy(''); } }} />
            </label>
            <ActionButton type="button" className="opt" disabled={busy || !why.trim()} onClick={save}>Save</ActionButton>
            <ActionButton type="button" className="hx-link m" onClick={() => { setPending(null); setWhy(''); setError(null); }}>Cancel</ActionButton>
            {error ? <SaveError words={error} retry={save} /> : null}
          </span>
        )
        : null}
    </>
  );
}

/**
 * The card's first line (P9; c-card.html): the keenness block and "Keenness 4, was 3 until Ana Ruiz's Teams message of
 * 6 Oct: “…”", with Open it, Undo while Cobrain's move can be undone, and Change, which opens the five buttons (U293).
 */
export function KeenLine({ row, ctx, showName, onSaved, onNote }: { row: HeatRow; ctx: LiveContext; showName: string | null; onSaved: (s: Saved) => void; onNote: (w: string | null) => void }) {
  const [changing, setChanging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const l = keennessLine(row, showName, ctx.dayStart);
  const path = l.message ? opensPath(l.message.signalId, l.message.opens, ctx.store) : null;
  const undo = () => {
    if (!l.move) return;
    setBusy(true); setError(null);
    dealsApi.undoChange(ctx.store, l.move.changeId)
      .then(() => { setBusy(false); onNote('Undone. Cobrain will not move it again from that message.'); ctx.refresh({ accountId: row.accountId, dealId: row.id }); })
      .catch((e: unknown) => { setBusy(false); setError(e instanceof DealsError ? `Nothing changed: ${e.message}.` : 'Nothing changed. Try again.'); });
  };
  return (
    <div className="lv-kline">
      <KeenBlock k={row.keenness} small />
      <span className="t">
        {l.text}
        {l.paragraph ? <span className="lv-call-reason">{l.paragraph}</span> : null}
        {l.meetingId ? <> <a className="hx-link" href={`/meetings/${l.meetingId}`} onClick={(e) => {
          if (e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
            e.preventDefault(); ctx.open(`/meetings/${l.meetingId}`);
          }
        }}>Open the call</a></> : null}
        {path ? <> <ActionButton reading type="button" className="hx-link" onClick={() => ctx.open(path)}>Open it</ActionButton></> : null}
        {l.move && ctx.mayEdit && undoable(l.move, ctx.now) ? <> <ActionButton type="button" className="hx-link" disabled={busy} onClick={undo}>Undo</ActionButton></> : null}
        {ctx.mayEdit ? <> <ActionButton type="button" className="hx-link" aria-expanded={changing} onClick={() => setChanging(!changing)}>{changing ? 'Leave it' : 'Change'}</ActionButton></> : null}
      </span>
      {changing ? <span className="lv-kchange"><KeennessPicker row={row} ctx={ctx} onSaved={(x) => { setChanging(false); onSaved(x); }} /></span> : null}
      {error ? <span className="lv-err" role="alert">{error}</span> : null}
    </div>
  );
}

/** The year's figure as a field (U220): amount, currency, a year, with Save; empty and saved clears it. */
function FigureField({ row, ctx, onSaved }: { row: HeatRow; ctx: LiveContext; onSaved: (s: Saved) => void }) {
  const was = row.figure ? String(Number(row.figure.amount)) : '';
  const [amount, setAmount] = useState(was);
  const [currency, setCurrency] = useState(row.figure?.currency ?? ctx.homeCurrency ?? ctx.currencies[0] ?? 'GBP');
  // The figure the read holds is the box's, whenever the read changes it: a save, an Undo, or the other page's edit.
  useEffect(() => { setAmount(was); if (row.figure) setCurrency(row.figure.currency); }, [was, row.figure]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const choices = [...new Set([currency, ...ctx.currencies])];
  const save = () => {
    const raw = amount.replace(/[,\s£€$]/g, '');
    if (raw !== '' && !/^\d+(\.\d{1,2})?$/.test(raw)) { setError('That is not an amount. Your figure is still in the box.'); return; }
    if (raw === '' && !row.figure) return;
    setBusy(true); setError(null);
    const n = raw === '' ? null : Number(raw);
    dealsApi.setFigure(ctx.store, row.id, n, n === null ? null : currency)
      .then((r) => { setBusy(false); onSaved({ field: 'figure', changeId: r.changeId, words: n === null ? 'You took the figure off just now.' : 'You set it just now.' }); ctx.refresh({ accountId: row.accountId, dealId: row.id }); })
      .catch((e: unknown) => { setBusy(false); setError(failed(e, 'Your figure is still in the box.')); });
  };
  return (
    <>
      <span className={error ? 'lv-field bad' : 'lv-field'}>
        {choices.length > 1
          ? <ActionSelect aria-label="Currency" value={currency} onChange={(e) => setCurrency(e.target.value)}>{choices.map((c) => <option key={c} value={c}>{c}</option>)}</ActionSelect>
          : <span className="cur">{currency}</span>}
        <ActionInput type="text" inputMode="decimal" aria-label="Revenue potential a year" value={amount} placeholder="amount"
          onChange={(e) => setAmount(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') save(); }} />
        <span className="yr">a year</span>
      </span>
      <ActionButton type="button" className="opt" disabled={busy || amount.trim() === was} onClick={save}>Save</ActionButton>
      {error ? <SaveError words={error} retry={save} /> : null}
    </>
  );
}

/**
 * A parked deal brought back (BUGS.md D63): the old desk's "Take it off the shelf" and its words, which the actions
 * contract kept (row 4, "Keep as it is"), under the parked step. POST /api/deals/:id/unpark puts it back open at the step
 * it sat at, its date and reason cleared. The old desk's card that called it is mounted nowhere since Deals became one list.
 */
function OffTheShelf({ row, ctx }: { row: HeatRow; ctx: LiveContext }) {
  const [busy, setBusy] = useState(false);
  const back = () => {
    setBusy(true);
    dealsApi.unpark(ctx.store, row.id)
      .then(() => { setBusy(false); ctx.setNote(row.id, { saved: null, note: 'Back on the paper where it was.' }); ctx.refresh({ accountId: row.accountId, dealId: row.id }); })
      .catch((e: unknown) => { setBusy(false); ctx.setNote(row.id, { saved: null, note: `That did not go through${e instanceof DealsError ? `: ${e.message.replace(/\.$/, '')}` : ''}. Nothing was changed.` }); });
  };
  return <ActionButton type="button" className="hx-link m lv-unpark" disabled={busy} onClick={back}>Take it off the shelf</ActionButton>;
}

/** The step box that leads the card (item 7): whose step and by when, Cobrain's reason, and the reader's own options. */
function StepBox({ row, lead, ctx }: { row: HeatRow; lead: Lead | null; ctx: LiveContext }) {
  const s = stepBoxWords(row, ctx.now);
  const messageLead = s.kind === 'none' && !!lead?.source;
  const mineLead = s.mine || messageLead ? lead : null;
  // The message that raised the step, drawn under the step line and above the options (module 10 U416).
  const thread = useCardThread(ctx.store, mineLead?.source?.signalId ?? null);
  // Reply and Reply all sit outside the options, so no slice of the options can cut them (module 10 U415, U416).
  const [reply, setReply] = useState<{ all: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const newest = thread?.messages.find((m) => m.id === thread.newestId) ?? thread?.messages.at(-1);
  const self = thread?.selfAddresses ?? [];
  return (
    <div className="lv-stepbox" data-kind={s.kind}>
      <span className="s">
        {messageLead && mineLead?.reason ? mineLead.reason : s.lead ? <><span className="lead">{s.lead}</span> {s.rest}</> : s.rest}{s.due && !s.late ? `, by ${s.due}` : ''}
        {s.due && s.late ? <span className="lv-plate"><span className="dot" />{s.due}</span> : null}
      </span>
      {mineLead?.reason && !messageLead ? <span className="why">{mineLead.reason}</span> : null}
      {mineLead?.source && thread ? (
        <CardMessage store={ctx.store} signalId={mineLead.source.signalId} channel={mineLead.source.channel} thread={thread} open={!!reply}>
          {reply && newest ? (
            <ReplyBox key={`${reply.all}`} item={{ id: mineLead.passItemId, store: ctx.store, source: mineLead.source }} message={newest} self={self} all={reply.all}
              onBusy={setBusy} onCancel={() => setReply(null)} onSent={(names, after) => { setReply(null); setSaid(`Sent in the thread to ${names}${after ? `. ${after}` : ''}`); }} />
          ) : null}
        </CardMessage>
      ) : null}
      {mineLead?.source && newest ? (
        <ActionRow variant="replies" actions={[
          { key: 'reply', label: 'Reply', disabled: busy, onClick: () => { if (mineLead.source?.channel === 'whatsapp') setSaid(WHATSAPP_REPLY_SOON); else setReply({ all: false }); } },
          ...(mineLead.source.channel === 'email' && canReplyAll(newest, self)
            ? [{ key: 'reply-all', label: 'Reply all', disabled: busy, onClick: () => setReply({ all: true }) }] : []),
        ]} />
      ) : null}
      {said ? <p className="note">{said}</p> : null}
      {ctx.mayEdit && row.lifecycle === 'paused' ? <OffTheShelf row={row} ctx={ctx} /> : null}
      <TeamDecisions row={row} ctx={ctx} />
      {ctx.mayEdit
        ? <DealOptions store={ctx.store} dealId={row.id} lead={mineLead} dayStart={ctx.dayStart} onOpenItem={() => { if (mineLead) ctx.open(itemPath(mineLead, ctx.store)); }} onChanged={() => ctx.refresh({ accountId: row.accountId, dealId: row.id })} />
        : mineLead
        ? (
          <div className="opts">
            {mineLead.options.slice(0, 2).map((o, i) => (
              <ActionButton type="button" key={o.key} className={i === 0 ? 'opt lead' : 'opt'} onClick={() => ctx.open(itemPath(mineLead, ctx.store))}>
                {o.label}{o.note ? <small>{o.note}</small> : null}
              </ActionButton>
            ))}
            <ActionButton type="button" className="hx-link m" onClick={() => ctx.open(itemPath(mineLead, ctx.store))}>Choose another step</ActionButton>
          </div>
        )
        : null}
    </div>
  );
}

/**
 * What the team decided about the deal (pipeline B8, U328), under its step: Cobrain's line of each decision, where and when
 * it was decided, "Open the meeting", and Undo while D62's week lasts. Nothing is drawn when there is none.
 */
function TeamDecisions({ row, ctx }: { row: HeatRow; ctx: LiveContext }) {
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ ok: boolean; words: string } | null>(null);
  const list = row.live?.decisions ?? [];
  if (list.length === 0 && !said) return null;
  const undo = (id: string) => {
    setBusy(true); setSaid(null);
    dealsApi.undoDecision(ctx.store, id)
      .then(() => { setBusy(false); setSaid({ ok: true, words: 'Undone. Cobrain will not write that again from the same message.' }); ctx.refresh({ accountId: row.accountId, dealId: row.id }); })
      .catch((e: unknown) => { setBusy(false); setSaid({ ok: false, words: e instanceof DealsError ? `Nothing changed: ${e.message}.` : 'Nothing changed. Try again.' }); });
  };
  return (
    <div className="lv-decided" role="group" aria-label="What the team decided">
      {list.map((x) => {
        const w = decisionWords(x, ctx.dayStart);
        const path = x.from ? opensPath(x.from.signalId, x.from.opens, ctx.store) : null;
        const noun = x.from?.kind === 'meeting' ? 'meeting' : x.from?.kind === 'call' ? 'call' : x.from?.kind === 'email' ? 'email' : 'message';
        return (
          <p key={x.id} className="d" data-kind={x.kind}>
            <span className="t"><b>{w.lead}</b> {w.words} <span className="src">{w.source}</span></span>
            {ctx.mayEdit && decisionUndoable(x, ctx.now)
              ? <ActionButton type="button" className="hx-link" disabled={busy} onClick={() => undo(x.id)}>Undo, until {dayMonth(new Date(x.undoUntil))}</ActionButton>
              : null}
            {path ? <ActionButton reading type="button" className="hx-link m" onClick={() => ctx.open(path)}>Open the {noun}</ActionButton> : null}
          </p>
        );
      })}
      {said ? <span className={said.ok ? 'lv-said' : 'lv-err'} role={said.ok ? 'status' : 'alert'}>{said.words}</span> : null}
    </div>
  );
}

/** Latest: the deal's newest messages filed under its matter, today's marked with the ink dot; each opens its item. */
function Latest({ row, ctx }: { row: HeatRow; ctx: LiveContext }) {
  const live = row.live;
  const latest = live?.latest ?? [];
  const today = latest.filter((m) => m.today).length;
  if (latest.length === 0 || today === 0) {
    const last = latest[0]?.at ?? null;
    return (
      <div className="lv-news">
        <h4>{latest.length === 0 && !live ? 'Latest' : 'Nothing new today'}<small>{last ? `last message ${dayMonth(new Date(last))}` : 'nothing filed under it yet'}</small></h4>
        {latest.map((m) => <MessageRow key={m.signalId} m={m} ctx={ctx} />)}
      </div>
    );
  }
  return (
    <div className="lv-news">
      <h4>Latest<small>{latestHead(latest, today)}</small></h4>
      {latest.map((m) => <MessageRow key={m.signalId} m={m} ctx={ctx} />)}
    </div>
  );
}

function MessageRow({ m, ctx }: { m: DealMessage; ctx: LiveContext }) {
  const path = opensPath(m.signalId, m.opens, ctx.store);
  const body: ReactNode = (
    <>
      <span className="chip">{kindChip(m.kind)}</span>
      <span className="w">{senderWords(m)}{m.fromMe && m.to ? ` to ${m.to}` : ''}</span>
      <span className="q">{subjectWords(m) ?? ''}</span>
      <span className="t">{dayWords(m.at, ctx.dayStart)}</span>
      {path ? <Chev /> : <span />}
    </>
  );
  // Our own messages and notes are as quiet as "You wrote" on the line (P10, section 4b).
  const cls = `lv-msg${m.today ? ' new' : ''}${m.fromMe ? ' you' : ''}`;
  return path
    ? <ActionButton reading type="button" className={cls} onClick={() => ctx.open(path)}>{body}</ActionButton>
    : <div className={cls}>{body}</div>;
}

/** The stage meter (not tabs), and "Correct the stage" opening the store's stages as choices. */
function StageRow({ row, ctx, onSaved }: { row: HeatRow; ctx: LiveContext; onSaved: (s: Saved) => void }) {
  const [choosing, setChoosing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const live = row.live;
  const set = (code: string, name: string) => {
    setBusy(true); setError(null);
    dealsApi.setStage(ctx.store, row.id, code)
      .then((r) => { setBusy(false); setChoosing(false); onSaved({ field: 'stage', changeId: r.changeId, words: `You put it at ${name} just now.` }); ctx.refresh({ accountId: row.accountId, dealId: row.id }); })
      .catch((e: unknown) => { setBusy(false); setError(failed(e, 'The stage is as it was.')); });
  };
  const steps = meterSteps(ctx.stages, live?.stageCode ?? null);
  return (
    <>
      <div className="lv-stagerow" data-stage={stageTone(live?.stageCode)}>
        {live && !live.stageRead
          ? <p className="lv-notread"><span className="l">Stage</span>Not read yet. Cobrain reads it from the mail when a proposal or a contract goes out.</p>
          : (
            <ol className="lv-meter" aria-label={`Stage: ${live?.stageName ?? 'none'}, ${Math.max(steps.findIndex((s) => s.state === 'at') + 1, 0)} of ${steps.length}`}>
              {steps.map((s) => <li key={s.code} className={s.state === 'ahead' ? '' : s.state}>{s.name}</li>)}
            </ol>
          )}
        {ctx.mayEdit && live
          ? <ActionButton type="button" className="hx-link m" aria-expanded={choosing} onClick={() => setChoosing(!choosing)}>{choosing ? 'Leave the stage as it is' : 'Correct the stage'}</ActionButton>
          : null}
      </div>
      {choosing
        ? (
          <div className="lv-choose" role="group" aria-label="Correct the stage">
            {steps.map((s) => (
              <ActionButton type="button" key={s.code} className={s.state === 'at' ? 'opt on' : 'opt'} aria-pressed={s.state === 'at'} disabled={busy || s.state === 'at'} onClick={() => set(s.code, s.name)}>{s.name}</ActionButton>
            ))}
          </div>
        )
        : null}
      {error ? <SaveError words={error} retry={() => setError(null)} /> : null}
    </>
  );
}

/** What Cobrain changed, from what, with Undo while D62's week lasts; once put back, the way to move it again (states.html). */
function Moved({ row, ctx, onSaved, onNote, stageOnly }: { row: HeatRow; ctx: LiveContext; onSaved: (s: Saved) => void; onNote: (w: string) => void; stageOnly?: boolean }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // On the card the keenness line says Cobrain's keenness move (P9): Moved says the stage's.
  const m = stageOnly ? row.live?.stageMove ?? (row.live?.lastMove?.field === 'stage' ? row.live.lastMove : null) : row.live?.lastMove;
  if (!m || !moveStillSays(m, { stageCode: row.live?.stageCode ?? null, keenness: row.keenness })) return null;
  const s = moveSentence(m, ctx.dayStart);
  const path = m.from ? opensPath(m.from.signalId, m.from.opens, ctx.store) : null;
  const refused = (e: unknown) => { setBusy(false); setError(e instanceof DealsError ? `Nothing changed: ${e.message}.` : 'Nothing changed. Try again.'); };
  // Undoing Cobrain's change is said and not offered back: "Move it to <stage> again" is the way back (states.html).
  const undo = () => {
    setBusy(true); setError(null);
    dealsApi.undoChange(ctx.store, m.changeId).then(() => { setBusy(false); onNote('Undone. Cobrain will not move it again from that message.'); ctx.refresh({ accountId: row.accountId, dealId: row.id }); }).catch(refused);
  };
  const again = (code: string, name: string | null) => {
    setBusy(true); setError(null);
    dealsApi.setStage(ctx.store, row.id, code).then((r) => { setBusy(false); onSaved({ field: 'stage', changeId: r.changeId, words: `You moved it to ${name ?? 'that stage'} again just now.` }); ctx.refresh({ accountId: row.accountId, dealId: row.id }); }).catch(refused);
  };
  const noun = m.from ? (m.from.kind === 'email' ? 'email' : m.from.kind === 'call' ? 'call' : m.from.kind === 'meeting' ? 'meeting' : 'message') : 'message';
  return (
    <div className="lv-moved" data-stage={m.field === 'stage' ? stageTone(m.undone ? m.fromCode : m.toCode) : undefined}>
      <span>{s.before}<b className={m.field === 'stage' ? 'stw' : undefined}>{s.value}</b>{s.after}</span>
      {ctx.mayEdit && undoable(m, ctx.now)
        ? <ActionButton type="button" className="hx-link" disabled={busy} onClick={undo}>Undo, until {dayMonth(new Date(m.undoUntil!))}</ActionButton>
        : null}
      {ctx.mayEdit && m.undone && m.field === 'stage' && m.toCode && m.toCode !== row.live?.stageCode
        ? <ActionButton type="button" className="hx-link" disabled={busy} onClick={() => again(m.toCode!, m.toName)}>Move it to {m.toName} again</ActionButton>
        : null}
      {path ? <ActionButton reading type="button" className="hx-link m" onClick={() => ctx.open(path)}>Open the {noun}</ActionButton> : null}
      {error ? <span className="lv-err" role="alert">{error}</span> : null}
    </div>
  );
}

/**
 * "Held by", and "Give the deal to someone" (U262: anyone in Commercial): a colleague from the whole team, the holder left
 * out. A deal somebody holds is handed on, one nobody holds is given; either says who holds it now, with Undo for a week.
 * Nobody is sent anything.
 */
function HeldBy({ row, ctx, label = true }: { row: HeatRow; ctx: LiveContext; label?: boolean }) {
  const [choosing, setChoosing] = useState(false);
  const [busy, setBusy] = useState(false);
  const holder = holderName(row);
  // A parked deal is given on too (BUGS.md D143): it stays in its holder's Mine (FT1) and the give and hand routes move it.
  const may = ctx.mayEdit && (row.lifecycle === 'open' || row.lifecycle === 'paused');
  const choices = ctx.giveTo.filter((t) => t.principalId !== row.holder?.principalId);
  const give = (to: { principalId: string; name: string }) => {
    setBusy(true);
    (row.holder ? dealsApi.hand(ctx.store, row.id, to.principalId) : dealsApi.give(ctx.store, row.id, to.principalId))
      .then((r) => { setBusy(false); setChoosing(false); ctx.setNote(row.id, { saved: { field: 'holder', changeId: r.actionId, words: handedWords(to, ctx.me) }, note: null }); ctx.refresh({ accountId: row.accountId, dealId: row.id }); })
      .catch((e: unknown) => { setBusy(false); ctx.setNote(row.id, { saved: null, note: `It was not given on${e instanceof DealsError ? `: ${e.message}` : ''}. It is held as it was.` }); });
  };
  return (
    <>
      <span className="lv-held">
        {label ? <><span className="l">Held by</span> </> : null}<b>{holder ?? 'Nobody yet'}</b>
        {may && choices.length > 0
          ? <ActionButton type="button" className="hx-link" aria-expanded={choosing} onClick={() => setChoosing(!choosing)}>Give the deal to someone</ActionButton>
          : null}
      </span>
      {choosing
        ? (
          <span className="lv-choose lv-giveto" role="group" aria-label="Who should hold it?">
            <span className="l">Who should hold it?</span>
            {choices.map((t) => <ActionButton type="button" key={t.principalId} className="opt" disabled={busy} onClick={() => give(t)}>{t.name}</ActionButton>)}
            <ActionButton type="button" className="hx-link m" onClick={() => setChoosing(false)}>Cancel</ActionButton>
          </span>
        )
        : null}
    </>
  );
}

/**
 * "Why is this here?" opened (U263): where the line sits on Deals and why, what breaks a tie at its keenness, where the
 * keenness came from and its place on the reader's list, with "Open the item" when it is on it.
 */
function WhyHere({ row, rank, ctx, show }: { row: HeatRow; rank: LineRank; ctx: LiveContext; show: ShowLine | null }) {
  const lead = ctx.leads[row.id] ?? null;
  const w = whyHereWords(row, rank, lead, show?.name ?? null, ctx.dayStart);
  return (
    <div className="lv-whyhere" role="region" aria-label="Why is this here?">
      <p><b>{w.head}</b></p>
      {w.lines.map((l) => <p key={l}>{l}</p>)}
      {lead ? <ActionButton reading type="button" className="hx-link m" onClick={() => ctx.open(itemPath(lead, ctx.store))}>Open the item</ActionButton> : null}
    </div>
  );
}

/** "Park it until…": a day still to come and a line why (the park route's own rules). */
function ParkIt({ row, ctx }: { row: HeatRow; ctx: LiveContext }) {
  const [openForm, setOpenForm] = useState(false);
  const [until, setUntil] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!openForm) return <ActionButton type="button" className="hx-link m" onClick={() => setOpenForm(true)}>Park it until…</ActionButton>;
  const park = () => {
    if (!until || !reason.trim()) return;
    setBusy(true); setError(null);
    dealsApi.park(ctx.store, row.id, new Date(`${until}T09:00:00`).toISOString(), reason.trim())
      .then(() => { setBusy(false); setOpenForm(false); ctx.refresh(); })
      .catch((e: unknown) => { setBusy(false); setError(failed(e, 'What you typed is still here.')); });
  };
  return (
    <span className="lv-park">
      <label><span className="l">Until</span><ActionInput type="date" value={until} min={new Date(Date.parse(ctx.now) + 86_400_000).toISOString().slice(0, 10)} onChange={(e) => setUntil(e.target.value)} /></label>
      <label><span className="l">Why</span><ActionInput type="text" value={reason} maxLength={300} placeholder="One line" onChange={(e) => setReason(e.target.value)} /></label>
      <ActionButton type="button" className="opt" disabled={busy || !until || !reason.trim()} onClick={park}>Park it</ActionButton>
      <ActionButton type="button" className="hx-link m" onClick={() => setOpenForm(false)}>Cancel</ActionButton>
      {error ? <SaveError words={error} retry={park} /> : null}
    </span>
  );
}

/** The show notes the opportunity came from (P4's facts), opened on a press. */
function SheetNotes({ row, show }: { row: HeatRow; show: ShowLine | null }) {
  const s = row.fromSheet;
  const facts: Array<[string, string]> = [];
  if (s?.nextStep) facts.push([show ? `Next step, ${show.name} notes` : 'Next step, from the notes', [s.nextStep, s.nextStepOwner, s.due].filter(Boolean).join(', ')]);
  if (s?.whoMovesNext) facts.push(['Who moves next', s.whoMovesNext]);
  if (s?.termsHeard) facts.push(['Terms heard', s.termsHeard]);
  if (s?.routeToLive) facts.push(['Route to live', s.routeToLive]);
  if (s?.metBy) facts.push([show ? `Met at ${show.name} by` : 'Met by', s.metBy]);
  for (const p of row.promises) facts.push(['They said they would', p.statement]);
  for (const o of row.objections) { facts.push(['They said', o.said ? `“${o.said.text}”` : o.name]); facts.push([usualAnswerLabel(o), o.teamAnswer ?? 'No usual answer yet']); }
  if (facts.length === 0) return null;
  return <dl className="lv-facts">{facts.map(([l, v], i) => <div key={`${l}-${i}`}><dt>{l}</dt><dd>{v}</dd></div>)}</dl>;
}

/**
 * One opportunity as a live card, top to bottom (P9; c-card.html; decision first, PRODUCT.md principle 1): the keenness
 * line; the step box; "In their words"; Value; the stage and what Cobrain changed of it; the foot.
 */
export function DealCard({ row, accountId, accountName, ctx, rank, titled }: { row: HeatRow; accountId: string; accountName: string | null; ctx: LiveContext; rank?: LineRank; titled?: boolean }) {
  const kept = ctx.notes[row.id];
  const saved = kept?.saved ?? null;
  const note = kept?.note ?? null;
  const [notes, setNotes] = useState(false);
  const [why, setWhy] = useState(false);
  // REQ-2026-10-08-13: the company record's note box, for this deal, opened from the foot.
  const [noteBox, setNoteBox] = useState(false);
  const [closing, setClosing] = useState<CloseKind | null>(null);
  const lead = ctx.leads[row.id] ?? null;
  const show = row.eventId ? ctx.shows.find((s) => s.id === row.eventId) ?? null : null;
  const onSaved = (s: Saved) => ctx.setNote(row.id, { saved: s, note: null });
  const onNote = (w: string | null) => ctx.setNote(row.id, w ? { saved: null, note: w } : null);
  return (
    <div className="lv-deal">
      {/* A card switched to another of the account's products names it: the line above names its own (P8). */}
      {titled ? <p className="lv-pn lv-ptitle">{row.product ?? row.title}</p> : null}
      <KeenLine row={row} ctx={ctx} showName={show?.name ?? null} onSaved={onSaved} onNote={onNote} />
      {saved && !saved.field.startsWith('close:') ? <SavedNote row={row} saved={saved} accountName={accountName} ctx={ctx} onUndone={onNote} /> : null}
      {note ? <p className="lv-said" role="status">{note}</p> : null}
      <StepBox row={row} lead={lead} ctx={ctx} />
      {/* The Deal work lane's "This looks like the same deal" (W3, U260), above what was said. */}
      {ctx.mayEdit ? <SameDeal store={ctx.store} dealId={row.id} onChanged={() => ctx.refresh({ accountId: row.accountId, dealId: row.id })} /> : null}
      <InTheirWords row={row} ctx={ctx} accountName={row.accountName ?? accountName} showName={show?.name ?? null} />
      <ValueBlock row={row} ctx={ctx} onSaved={onSaved} onNote={onNote} showName={show?.name ?? null} />
      <StageRow row={row} ctx={ctx} onSaved={onSaved} />
      <Moved row={row} ctx={ctx} onSaved={onSaved} onNote={onNote} stageOnly />
      <div className="lv-dfoot">
        {/* The history page (step 7, U239), with the count of what is filed under the deal. */}
        <ActionButton reading type="button" className="hx-link" onClick={() => ctx.open(`/deals/${encodeURIComponent(row.id)}${row.side === 'rights' ? '?view=history' : ''}`)}>Everything on this deal{row.live && row.live.messages > 0 ? `, ${row.live.messages}` : ''}</ActionButton>
        <ActionButton type="button" className="hx-link m" aria-expanded={noteBox} onClick={() => setNoteBox(!noteBox)}>{noteBox ? 'Close the note box' : 'Add a note'}</ActionButton>
        {ctx.place === 'deals'
          ? <ActionButton reading type="button" className="hx-link m" onClick={() => ctx.open(`/companies/${encodeURIComponent(row.organisationId)}`)}>Open the company</ActionButton>
          : <ActionButton reading type="button" className="hx-link m" onClick={() => ctx.open(`/deals?open=${encodeURIComponent(row.id)}`)}>See it on Deals</ActionButton>}
        {ctx.mayEdit ? <label className="lv-prod"><span className="l">Product</span><ProductSelect row={row} ctx={ctx} /></label> : null}
        <HeldBy row={row} ctx={ctx} />
        {row.fromSheet || row.promises.length + row.objections.length > 0
          ? <ActionButton reading type="button" className="hx-link m" aria-expanded={notes} onClick={() => setNotes(!notes)}>{notes ? 'Hide the notes' : show ? `From the ${show.name} notes` : 'From the notes'}</ActionButton>
          : null}
        {ctx.mayEdit && row.lifecycle === 'open' ? <>
          <ActionButton type="button" className="opt" aria-expanded={closing === 'won'} onClick={() => setClosing(closing === 'won' ? null : 'won')}>Mark it won</ActionButton>
          <ActionButton type="button" className="opt" aria-expanded={closing === 'lost'} onClick={() => setClosing(closing === 'lost' ? null : 'lost')}>Mark it lost</ActionButton>
          <ParkIt row={row} ctx={ctx} />
        </> : null}
        {/* On Deals only: the company record's card has no "Why is this here?" (the brief, section 5 item 8; U263). */}
        {ctx.place === 'deals' && rank
          ? <ActionButton reading type="button" className="hx-link m" aria-expanded={why} onClick={() => setWhy(!why)}>Why is this here?</ActionButton>
          : null}
      </div>
      {closing ? <CloseBox key={closing} kind={closing} row={row} ctx={ctx} cancel={() => setClosing(null)} /> : null}
      {noteBox
        ? <DealNotes storeId={ctx.store} dealId={row.id} account={row.accountName ?? accountName} product={row.product} open={ctx.open}
            onAdded={() => ctx.refresh({ accountId: row.accountId, dealId: row.id })} />
        : null}
      {why && rank ? <WhyHere row={row} rank={rank} ctx={ctx} show={show} /> : null}
      {notes ? <SheetNotes row={row} show={show} /> : null}
      {row.lastHeardAt ? <p className="lv-meta">Last heard {dayWords(row.lastHeardAt, ctx.dayStart)}{row.firstContactAt && row.firstContactAt !== row.lastHeardAt ? `, first contact ${dayMonth(new Date(row.firstContactAt))}` : ''}</p> : null}
    </div>
  );
}

/** Who holds an opportunity, as the card's "Held by" says it. */
const holderName = (row: HeatRow): string | null => (row.holder ? (row.holder.mine ? 'You' : row.holder.name ?? 'A colleague') : null);

/** The product, chosen among the store's families, the current one first when it is none of them. */
function ProductSelect({ row, ctx, label }: { row: HeatRow; ctx: LiveContext; label?: string }) {
  const [busy, setBusy] = useState(false);
  const families = [...new Set([...(row.product && !ctx.families.includes(row.product) ? [row.product] : []), ...ctx.families])];
  const setProduct = (p: string) => {
    setBusy(true);
    dealsApi.setProduct(ctx.store, row.id, p)
      .then((r) => { setBusy(false); ctx.setNote(row.id, { saved: { field: 'product', changeId: r.changeId, words: `You made it ${p} just now.` }, note: null }); ctx.refresh({ accountId: row.accountId, dealId: row.id }); })
      .catch((e: unknown) => { setBusy(false); ctx.setNote(row.id, { saved: ctx.notes[row.id]?.saved ?? null, note: failed(e, 'The product is as it was.') }); });
  };
  return (
    <ActionSelect aria-label={label} value={row.product ?? ''} disabled={busy} onChange={(e) => { if (e.target.value && e.target.value !== row.product) setProduct(e.target.value); }}>
      {row.product ? null : <option value="">None yet</option>}
      {families.map((f) => <option key={f} value={f}>{f}</option>)}
    </ActionSelect>
  );
}

/**
 * The history page's top (b-deal-page.html; the brief, section 5 item 9): the stage, Latest, what Cobrain changed and
 * the step, then the facts (keenness with where it came from, the year's figure, the product, who holds it). The card's
 * own parts in the drawing's order, so an edit here is the card's edit, with its "Saved" and Undo.
 */
export function DealTop({ row, accountName, ctx }: { row: HeatRow; accountName: string | null; ctx: LiveContext }) {
  const kept = ctx.notes[row.id];
  const onSaved = (s: Saved) => ctx.setNote(row.id, { saved: s, note: null });
  const onNote = (w: string | null) => ctx.setNote(row.id, w ? { saved: null, note: w } : null);
  const show = row.eventId ? ctx.shows.find((s) => s.id === row.eventId) ?? null : null;
  return (
    <div className="lv-rtop">
      <StageRow row={row} ctx={ctx} onSaved={onSaved} />
      <Latest row={row} ctx={ctx} />
      <Moved row={row} ctx={ctx} onSaved={onSaved} onNote={onNote} />
      <StepBox row={row} lead={ctx.leads[row.id] ?? null} ctx={ctx} />
      <div className="lv-facts2">
        <div className="lv-drow" id="dp-keenness">
          <span className="l">Keenness</span>
          <span className="v">
            {ctx.mayEdit ? <KeennessPicker row={row} ctx={ctx} onSaved={onSaved} /> : <KeenBlock k={row.keenness} small />}
            <span className="by">{keennessByWords(row, show?.name ?? null, ctx.dayStart)}</span>
          </span>
        </div>
        <div className="lv-drow">
          <span className="l">Revenue potential</span>
          <span className="v">{ctx.mayEdit ? <FigureField row={row} ctx={ctx} onSaved={onSaved} /> : <b>{row.figure ? `${moneyFull(row.figure.amount, row.figure.currency)} a year` : 'No figure yet'}</b>}</span>
        </div>
        <div className="lv-drow">
          <span className="l">Product</span>
          <span className="v">{ctx.mayEdit ? <ProductSelect row={row} ctx={ctx} label="Product" /> : <b>{row.product ?? 'None yet'}</b>}</span>
        </div>
        <div className="lv-drow">
          <span className="l">Held by</span>
          <span className="v"><HeldBy row={row} ctx={ctx} label={false} /></span>
        </div>
        {/* BUGS.md D73 (Mike, by pop-up, 8 Oct about 12:37 CEST: "show them; the contact in the card's facts and the deal
            page's head"): Add deal's "Their contact", opening their page. */}
        {row.contact
          ? (
            <div className="lv-drow">
              <span className="l">Their contact</span>
              <span className="v"><ActionButton reading type="button" className="hx-link" onClick={() => ctx.open(`/people/${encodeURIComponent(row.contact!.id)}`)}>{row.contact.name}</ActionButton></span>
            </div>
          )
          : null}
      </div>
      {kept?.saved ? <SavedNote row={row} saved={kept.saved} accountName={accountName} ctx={ctx} onUndone={onNote} /> : null}
      {kept?.note ? <p className="lv-said" role="status">{kept.note}</p> : null}
    </div>
  );
}

/**
 * An opened line (the brief, section 3): P5's card for the opportunity, and beside it from 1100 wide (above it under
 * that) "<Account>'s products": every open opportunity at the account, each with its keenness, stage and owner, one press
 * switching the card to it, and "Add a product" (U259, the Deal work lane's, handed in as `add`) under them.
 */
export function OpenedLine({ row, products, cardId, setCard, ctx, rankOf, bodyId, add }: {
  row: HeatRow; products: HeatRow[]; cardId: string; setCard: (id: string) => void; ctx: LiveContext;
  rankOf: (id: string) => LineRank | undefined; bodyId: string; add?: ReactNode;
}) {
  const card = products.find((r) => r.id === cardId) ?? row;
  const title = `${accountPossessive(row.accountName)} products`;
  const rank = rankOf(card.id);
  const line = useCompanyLine(ctx.store, row.organisationId);
  return (
    <div className="lv-obody" id={bodyId}>
      <div className="lv-ocard"><DealCard row={card} accountId={row.accountId} accountName={row.accountName} ctx={ctx} titled={card.id !== row.id} {...(rank ? { rank } : {})} /></div>
      <aside className="lv-panel lv-prods" aria-label={title}>
        {/* The account's line beside the deal card (U353; brief section 5: Deals takes the line), its name a link (X30). */}
        {/* BUGS.md D97: no line for a company or an account put away, which is named nowhere. */}
        {row.organisationId && row.organisationName !== null && row.accountName !== null ? <div className="lv-coline"><LineOrName id={row.organisationId} name={row.accountName} line={line} go={ctx.open} /></div> : null}
        <div className="lv-ph"><h3>{title}</h3><span className="hx-meta">{productsCountWords(products)}</span></div>
        {products.map((r) => (
          <ActionButton reading type="button" key={r.id} className={r.id === card.id ? 'lv-pv lv-pp on' : 'lv-pv lv-pp'} data-stage={stageTone(r.live?.stageCode)} aria-pressed={r.id === card.id} onClick={() => setCard(r.id)}>
            <KeenBlock k={r.keenness} small />
            <span className="x">
              <span className="n">{r.product ?? r.title}</span>
              <span className="st"><span className="stw">{r.live?.stageName ?? 'No stage'}</span> <Track stages={ctx.stages} code={r.live?.stageCode ?? null} /></span>
              <span className="o">{ownerOf(r).full}</span>
              {/* BUGS.md D144: a parked product says so here, as its own line and step box do. */}
              {parkedWords(r) ? <span className="o lv-pk">{parkedWords(r)!.words}</span> : null}
            </span>
          </ActionButton>
        ))}
        {add}
      </aside>
    </div>
  );
}
